import type { QueryKey } from '@tanstack/react-query';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import {
  extractAssetGroupId,
  makeCacheKey,
  type AssetGroupId,
  type AssetMetadataV2,
  type ChainAssetMarketData,
  type GroupAssetMarketData,
  type LstMetadataByAssetId,
} from '@infinex/asset-config';
import type { ChainKey } from '@infinex/chain-config';
import type {
  FilterAllTokensInput,
  FilterAllTokensOutput,
  GetAssetGroupChartInput,
  GetAssetGroupChartOutput,
  GetAssetGroupMarketDataOutput,
  AssetGroupMarketData as AssetGroupMarketDataResponse,
  GetOfficialLstChainAssetsOutput,
  GetTokenCategoryInput,
  GetTokenCategoryOutput,
  GetUserAssetPreferencesInput,
  GetUserAssetPreferencesOutput,
  SearchTokensInput,
  SearchTokensOutput,
} from '@infinex/main-service-contract/v2/client/assets';
import type {
  GetUserAssetsInput,
  GetUserAssetsOutput,
} from '@infinex/main-service-contract/v2/server/assets';
import { useActiveFrontendChainKeys, useAuthedQuery } from '@infinex/user';

import { filterGroupedAssetMembers } from '../../utils/index';
import type { QueryOptions, QueryOptionsV2 } from '../config';
import { getApiClientV2, getTRPCClient } from '../config';

export const useOfficialLstChainAssetsQuery = (
  options?: Omit<QueryOptionsV2<GetOfficialLstChainAssetsOutput>, 'select'>
) =>
  useQuery(
    getTRPCClient().assets.getOfficialLstChainAssets.queryOptions(undefined, {
      select: lstChainAssetSelector,
      ...options,
    })
  );

const lstChainAssetSelector = ({
  lstChainAssets,
}: GetOfficialLstChainAssetsOutput) => {
  const lstAssetsByAssetId = lstChainAssets.reduce((acc, asset) => {
    acc[asset.assetId] = asset.lstAssetMetadata;
    return acc;
  }, {} as LstMetadataByAssetId);

  return {
    lstChainAssets,
    lstAssetsByAssetId,
  };
};

/**
 * Get the shared **essential** metadata for an asset across all chains, either by the assetId if known,
 * or a member's chain and address if thats what you have.
 *
 * This will first look through our asset config to see if it can just return from there
 * if not, it will go to the server to get the metadata.
 *
 * example usage:
 * ```ts
 * const { data: essentialAssetMetadata } = useAssetMetadataQuery({ assetId: 'usd_coin' });
 * // OR
 * const { data: essentialAssetMetadata } = useAssetMetadataQuery({ chain: 'arbitrum', address: '0xa0b862F60edEf4452F25B4160F177db44DeB6Cf1' });
 * // ...
 * // essentialAssetMetadata.groupId will be either { chain, address } or { assetId }
 * // other essential metadata fields will remain the same
 * // essentialAssetMetadata.members will contain a map of all chain implementations of the group
 * ```
 */
// Mirrors apps/web-app/src/api_v2/assets/_constants.ts.
const ASSET_METADATA_STALE_TIME_MS = 5 * 60 * 1000;

export const useAssetMetadataQuery = (
  groupId: AssetGroupId | null | undefined,
  options?: QueryOptions<AssetMetadataV2 | null, QueryKey>
) => {
  const activeChainKeys = useActiveFrontendChainKeys();

  const lookup = useMemo(
    () => (groupId ? extractAssetGroupId(groupId) : null),
    [groupId]
  );

  return useQuery({
    queryKey: ['useAssetMetadataQuery', lookup, activeChainKeys] as const,
    queryFn: lookup
      ? async (): Promise<AssetMetadataV2 | null> => {
          const data = await getApiClientV2().assets.getAssetMetadata.query({
            lookup,
          });
          return data ? filterGroupedAssetMembers(data, activeChainKeys) : null;
        }
      : skipToken,
    staleTime: ASSET_METADATA_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    ...options,
  });
};

export const useBuildFetchSwidgeAssetMetadataApi = () => {
  const activeChainKeys = useActiveFrontendChainKeys();

  const getSwidgeAssetMetadataQueryOptions = useCallback(
    (groupId: AssetGroupId) => {
      const lookup = extractAssetGroupId(groupId);

      return {
        // activeChainKeys stays OUT of the key: it changes when the
        // feature-flag dataset lands (upcomingChainsEnabled), which would
        // orphan every warm entry at the exact moment synchronous readers
        // (the fast-mode member remap's getQueryData) need them. The raw
        // group caches once per lookup; member filtering happens in
        // `select`, so useQuery subscribers still see only active chains
        // while fetchQuery/getQueryData readers get the raw group.
        queryKey: ['getSwidgeAssetMetadata', lookup] as const,
        queryFn: async (): Promise<AssetMetadataV2 | null> => {
          const data = await getApiClientV2().assets.getAssetMetadata.query({
            lookup,
          });

          return data ?? null;
        },
        select: (data: AssetMetadataV2 | null): AssetMetadataV2 | null =>
          data ? filterGroupedAssetMembers(data, activeChainKeys) : null,
        // Same TTL as useAssetMetadataQuery — without this, default staleTime: 0
        // marks members immediately stale so shared subscribers refetch and the
        // sync remap path can miss a warm cache on click.
        staleTime: ASSET_METADATA_STALE_TIME_MS,
      };
    },
    [activeChainKeys]
  );

  return {
    getSwidgeAssetMetadataQueryOptions,
  };
};

export const useAssetMarketDataQuery = (search: AssetGroupId | null) => {
  const lookup = useMemo(
    () => (search ? extractAssetGroupId(search) : null),
    [search]
  );

  const selectFn = useCallback(
    (data: GetAssetGroupMarketDataOutput) => {
      if (!lookup) return null;

      return selectAssetGroupMarketData(data, lookup);
    },
    [lookup]
  );

  return useQuery(
    getTRPCClient().assets.getAssetGroupMarketData.queryOptions(
      { lookups: lookup ? [lookup] : [] },
      {
        enabled: lookup !== null,
        select: selectFn,
      }
    )
  );
};

export const useAssetGroupMarketDataQuery = (search: AssetGroupId | null) => {
  const lookup = useMemo(
    () => (search ? extractAssetGroupId(search) : null),
    [search]
  );

  const selectFn = useCallback(
    (data: GetAssetGroupMarketDataOutput) => {
      if (!lookup) return null;

      return selectAssetGroupMarketDataEnvelope(data, lookup);
    },
    [lookup]
  );

  return useQuery(
    getTRPCClient().assets.getAssetGroupMarketData.queryOptions(
      { lookups: lookup ? [lookup] : [] },
      {
        enabled: lookup !== null,
        select: selectFn,
      }
    )
  );
};

/**
 * Batch market data for a set of asset groups (e.g. every row of an
 * asset-select list in one request). The result is the raw envelope map
 * keyed by `makeCacheKey(extractAssetGroupId(groupId))`.
 */
export const useAssetGroupsMarketDataQuery = (groupIds: AssetGroupId[]) => {
  const lookups = useMemo(() => groupIds.map(extractAssetGroupId), [groupIds]);

  return useQuery(
    getTRPCClient().assets.getAssetGroupMarketData.queryOptions(
      { lookups },
      { enabled: lookups.length > 0 }
    )
  );
};

/**
 * The canonical market data for one lookup from the batched
 * `getAssetGroupMarketData` response: group-level provider data when the
 * group has it, otherwise the looked-up chain member's (falling back to the
 * primary chain, then any member). Exported for non-hook consumers (e.g. the
 * funds-received claimable mapping).
 */
export function selectAssetGroupMarketData(
  response: GetAssetGroupMarketDataOutput,
  lookup: AssetGroupId
): GroupAssetMarketData | null {
  const groupMarketData = response[makeCacheKey(lookup)];
  if (!groupMarketData) return null;

  const selectedMember =
    ('chain' in lookup ? groupMarketData.members[lookup.chain] : undefined) ??
    (groupMarketData.primaryChain
      ? groupMarketData.members[groupMarketData.primaryChain]
      : undefined) ??
    Object.values(groupMarketData.members).find(Boolean);

  if (groupMarketData.groupMarketData) {
    return {
      ...groupMarketData.groupMarketData,
      liquidity: selectedMember?.marketData?.liquidity ?? null,
    };
  }

  return selectedMember
    ? toGroupAssetMarketData(selectedMember.marketData)
    : null;
}

function selectAssetGroupMarketDataEnvelope(
  response: GetAssetGroupMarketDataOutput,
  lookup: AssetGroupId
): AssetGroupMarketDataResponse | null {
  return response[makeCacheKey(lookup)] ?? null;
}

function toGroupAssetMarketData(
  marketData: ChainAssetMarketData
): GroupAssetMarketData {
  return {
    chain: marketData.chain,
    address: marketData.address,
    rate: marketData.rate,
    marketCap: marketData.marketCap,
    fdv: marketData.fdv,
    volume24h: marketData.volume24h,
    circulatingSupply: marketData.circulatingSupply,
    totalSupply: marketData.totalSupply,
    maxSupply: marketData.maxSupply,
    rank: null,
    priceChange24h: marketData.priceChange24h,
    sparkline7d: null,
    liquidity: marketData.liquidity,
  };
}

/**
 * Search for tokens by name, symbol, or address.
 *
 */
export const useSearchTokensQuery = (
  params: SearchTokensInput,
  options?: QueryOptionsV2<SearchTokensOutput>
) => {
  const activeChainKeys = useActiveFrontendChainKeys();

  const selectFn = useCallback(
    (assets: SearchTokensOutput) => {
      return assets.flatMap((asset) => {
        return filterGroupedAssetMembers(asset, activeChainKeys) ?? [];
      });
    },
    [activeChainKeys]
  );

  return useQuery(
    getTRPCClient().assets.searchTokens.queryOptions(params, {
      select: selectFn,
      ...options,
    })
  );
};

// Codex-backed all-token search; the backend caches results for 2 minutes, so
// anything tighter than this buys nothing.
const FILTER_ALL_TOKENS_STALE_TIME_MS = 30 * 1000;
const FILTER_ALL_TOKENS_REFETCH_INTERVAL_MS = 60 * 1000;

export type FilterAllTokensToken = FilterAllTokensOutput['tokens'][number];

function pruneFilterAllTokensResult(
  token: FilterAllTokensToken,
  activeChainKeys: ChainKey[]
): FilterAllTokensToken | null {
  // Mirror filterGroupedAssetMembers, then also prune flags/marketData —
  // that helper only rebuilds members and spreads the rest.
  const members = Object.fromEntries(
    Object.entries(token.members).filter(
      ([chain, member]) =>
        !!member && activeChainKeys.includes(chain as ChainKey)
    )
  ) as FilterAllTokensToken['members'];

  if (Object.keys(members).length === 0) return null;

  const survivingChains = new Set(Object.keys(members) as ChainKey[]);

  return {
    ...token,
    members,
    flags: Object.fromEntries(
      Object.entries(token.flags).filter(([chain]) =>
        survivingChains.has(chain as ChainKey)
      )
    ) as FilterAllTokensToken['flags'],
    marketData: Object.fromEntries(
      Object.entries(token.marketData).filter(([chain]) =>
        survivingChains.has(chain as ChainKey)
      )
    ) as FilterAllTokensToken['marketData'],
  };
}

/**
 * Codex phrase/filter search over all tokens (official + non-official).
 * Returns AssetMetadataV2-based groups with per-chain market data.
 */
export const useFilterAllTokensQuery = (
  input: FilterAllTokensInput | null,
  options?: QueryOptionsV2<FilterAllTokensOutput>
) => {
  // Released + upcomingChainsEnabled — must be sent to Codex so unreleased
  // / testnet chains do not consume the result limit and get pruned later.
  // Preserve explicit `chains: []` (e.g. memecoin-mode empty intersection).
  const activeChainKeys = useActiveFrontendChainKeys();
  const resolvedInput = useMemo(() => {
    if (input === null) return null;
    return {
      ...input,
      chains: input.chains ?? activeChainKeys,
    };
  }, [input, activeChainKeys]);

  const selectFn = useCallback(
    (data: FilterAllTokensOutput): FilterAllTokensOutput => {
      const tokens = data.tokens.flatMap((token) => {
        const pruned = pruneFilterAllTokensResult(token, activeChainKeys);
        return pruned ? [pruned] : [];
      });
      return { tokens, count: tokens.length };
    },
    [activeChainKeys]
  );

  return useQuery(
    getTRPCClient().assets.filterAllTokens.queryOptions(resolvedInput ?? {}, {
      select: selectFn,
      staleTime: FILTER_ALL_TOKENS_STALE_TIME_MS,
      refetchInterval: FILTER_ALL_TOKENS_REFETCH_INTERVAL_MS,
      refetchOnWindowFocus: false,
      enabled: resolvedInput !== null,
      ...options,
    })
  );
};

/**
 * Get a category of tokens by type.
 *
 */
export const useGetTokensInCategoryQuery = (
  params: GetTokenCategoryInput,
  options?: QueryOptionsV2<GetTokenCategoryOutput>
) => {
  const activeChainKeys = useActiveFrontendChainKeys();

  const selectFn = useCallback(
    (assets: GetTokenCategoryOutput) => {
      return assets.flatMap((asset) => {
        return filterGroupedAssetMembers(asset, activeChainKeys) ?? [];
      });
    },
    [activeChainKeys]
  );

  return useQuery(
    getTRPCClient().assets.getTokenCategory.queryOptions(params, {
      select: selectFn,
      ...options,
    })
  );
};

export const useAssetGroupChartQuery = (
  params: {
    lookup: AssetGroupId | null | undefined;
    lookback?: GetAssetGroupChartInput['lookback'];
  },
  options?: QueryOptionsV2<GetAssetGroupChartOutput>
) => {
  return useQuery(
    getTRPCClient().assets.getAssetGroupChart.queryOptions(
      params.lookup
        ? {
            lookup: extractAssetGroupId(params.lookup),
            lookback: params.lookback,
          }
        : skipToken,
      options
    )
  );
};

export const ASSET_PREFERENCES_FILTER_BY_HIDDEN_PAYLOAD = {
  preferenceName: 'is_hidden',
} as const;

export const ASSET_PREFERENCES_FILTER_BY_FAVORITE_PAYLOAD = {
  preferenceName: 'is_favorite',
} as const;

export const useGetUserAssetPreferencesQuery = (
  params?: GetUserAssetPreferencesInput,
  options?: QueryOptionsV2<GetUserAssetPreferencesOutput>
) => {
  return useAuthedQuery(
    getTRPCClient().assets.getUserAssetPreferences.queryOptions(params, options)
  );
};

export const useGetUserAssetsQuery = (params?: GetUserAssetsInput) => {
  const formatNumber = useCallback((items: GetUserAssetsOutput) => {
    return items.map((item) => ({
      ...item,
      formattedBalance: Number(item.balance) / 10 ** item.decimals,
    }));
  }, []);

  return useQuery(
    getTRPCClient().assets.getUserAssets.queryOptions(params, {
      select: formatNumber,
    })
  );
};
